import { optionService } from '../services/option';

export interface CommissionRate {
  id: string;
  customerType: string;
  rate: number;
  description?: string;
}

export interface OrderFinancialData {
  price?: string | number;
  cost?: string | number;
  client_type?: string;
}

export interface CalculationResult {
  profit: number;
  commission: number;
  commissionRate: number;
  hasError: boolean;
  errorMessage?: string;
}

/**
 * 提成计算器类
 */
export class CommissionCalculator {
  private static commissionRates: CommissionRate[] = [];
  private static lastLoadTime = 0;
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

  /**
   * 加载提成比例配置
   */
  private static async loadCommissionRates(): Promise<void> {
    const now = Date.now();
    // 如果缓存未过期，直接使用缓存
    if (this.commissionRates.length > 0 && (now - this.lastLoadTime) < this.CACHE_DURATION) {
      return;
    }

    try {
      const rates = await optionService.get('commission_rates', []);

      if (Array.isArray(rates)) {
        // 处理新格式和旧格式数据
        if (rates.length > 0 && typeof rates[0] === 'string') {
          // 旧格式，转换为新格式
          this.commissionRates = rates.map((rate, index) => ({
            id: `rate_${index}`,
            customerType: 'A', // 默认类型
            rate: parseFloat(rate) || 0,
            description: ''
          }));
        } else {
          // 新格式
          this.commissionRates = rates;
        }
      } else {
        this.commissionRates = [];
      }

      this.lastLoadTime = now;
    } catch (error) {
      console.error('Failed to load commission rates:', error);
      this.commissionRates = [];
    }
  }

  /**
   * 根据客户等级获取提成比例
   */
  private static getCommissionRate(clientType: string): number {
    const rateConfig = this.commissionRates.find(
      config => config.customerType === clientType
    );

    return rateConfig ? rateConfig.rate : 0;
  }

  /**
   * 安全的数字转换
   */
  private static safeParseFloat(value: string | number | undefined): number {
    if (typeof value === 'number') {
      return isNaN(value) ? 0 : value;
    }

    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/[^\d.-]/g, ''));
      return isNaN(parsed) ? 0 : parsed;
    }

    return 0;
  }

  /**
   * 计算利润和提成
   * @param orderData 订单财务数据
   * @returns 计算结果
   */
  public static async calculate(orderData: OrderFinancialData): Promise<CalculationResult> {
    // 加载提成配置
    await this.loadCommissionRates();

    const price = this.safeParseFloat(orderData.price);
    const cost = this.safeParseFloat(orderData.cost);
    const clientType = orderData.client_type || '';

    // 计算利润
    const profit = price - cost;

    // 获取提成比例
    const commissionRate = this.getCommissionRate(clientType);

    // 计算提成
    const commission = profit * (commissionRate / 100);

    // 验证数据合理性
    let hasError = false;
    let errorMessage = '';

    if (price < 0) {
      hasError = true;
      errorMessage = '成交价格不能为负数';
    } else if (cost < 0) {
      hasError = true;
      errorMessage = '成本不能为负数';
    } else if (profit < 0) {
      hasError = true;
      errorMessage = '利润为负数，请检查价格和成本设置';
    } else if (!clientType) {
      hasError = true;
      errorMessage = '未设置客户等级，无法计算提成';
    } else if (commissionRate === 0) {
      hasError = true;
      errorMessage = `客户等级"${clientType}"未配置提成比例`;
    }

    return {
      profit: Math.round(profit * 100) / 100, // 保留2位小数
      commission: Math.round(commission * 100) / 100,
      commissionRate,
      hasError,
      errorMessage
    };
  }

  /**
   * 批量计算多个订单的提成
   */
  public static async batchCalculate(orders: OrderFinancialData[]): Promise<CalculationResult[]> {
    await this.loadCommissionRates();

    return Promise.all(orders.map(order => this.calculate(order)));
  }

  /**
   * 清除缓存（用于重新加载配置）
   */
  public static clearCache(): void {
    this.commissionRates = [];
    this.lastLoadTime = 0;
  }

  /**
   * 获取当前加载的提成配置（用于调试）
   */
  public static async getLoadedRates(): Promise<CommissionRate[]> {
    await this.loadCommissionRates();
    return [...this.commissionRates];
  }
}

/**
 * 便捷的计算函数
 */
export const calculateCommission = CommissionCalculator.calculate.bind(CommissionCalculator);

/**
 * 实时计算提成的Hook（如果需要在组件中使用）
 */
export const useCommissionCalculator = () => {
  const calculate = async (orderData: OrderFinancialData) => {
    return await CommissionCalculator.calculate(orderData);
  };

  const clearCache = () => {
    CommissionCalculator.clearCache();
  };

  return {
    calculate,
    clearCache
  };
};
